import { CentroCosto } from "core/centrocosto/models/CentroCosto";
import { generateReportFileName } from "core/reporte/util/fileNameGenerator";
import { useCoreService, useReporteService } from "core/services/useCoreService";
import { useCallback, useEffect, useMemo, useState } from "react";
import { SelectOption } from "shared/form/models/SelectOption";
import { useFileDownloader } from "shared/hooks/useFileDownloader";
import { useNotifications } from "shared/notifications";
import { ReportFormActions } from "../ReportFormActions/ReportFormActions";
import { FormatSelector } from "../FormatSelector/FormatSelector";
import { Col, Row } from "react-bootstrap";
import { Select } from "shared/form/components/Select";
import { Periodo } from 'shared/models/Periodo';
import { generatePeriods } from "shared/form/data/generatePeriods";
import { ReportFormat } from '../../models/ReportModels'

interface FormData {
  centroCostoId: number | null;
  formato: SelectOption<ReportFormat> | null;
  periodo: Periodo | null;
}


export const RendicionPorCentroCostoFrom = () => {
  const { generateRendicionPorCentroDeCostoReport } = useReporteService();
  const { addNotification } = useNotifications();
  const coreService = useCoreService();
  const { downloadFile } = useFileDownloader();
  const [centroCostosOptions, setCentroCostosOptions] = useState<SelectOption<number>[]>([]);
  const [allCentroCostos, setAllCentroCostos] = useState<CentroCosto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCentroCostos, setIsLoadingCentroCostos] = useState(true);

  const periodOptions = useMemo(
    () =>
      generatePeriods().filter(
        (option) => option.label !== 'Periodo Actual'
      ),
    [],
  );

  const [formData, setFormData] = useState<FormData>({
    centroCostoId: null,
    formato: null,
    periodo: null,
  });


  useEffect(() => {
    const fetchCostCentres = async () => {
      setIsLoadingCentroCostos(true);
      try {
        const response = await coreService.getCostCentres();
        setAllCentroCostos(response.content);
        setCentroCostosOptions(
          response.content.map(
            (cc: CentroCosto) =>
              ({ value: cc.id, label: cc.nombre }),
          ),
        );
      } catch (error) {
        addNotification('No se pudieron cargar los Centro de Costos.', 'error');
        setCentroCostosOptions([]);
        setAllCentroCostos([]);
      } finally {
        setIsLoadingCentroCostos(false);
      }
    };
    fetchCostCentres();
  }, [coreService, addNotification]);

  const handleInputChange = useCallback((name: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);


  const handleGenerateReport = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    if (!formData.centroCostoId || !formData.formato || !formData.periodo) {
      addNotification(
        'Debe seleccionar un Centro de costo, un período y un formato.',
        'warning',
      );
      setIsLoading(false);
      return;
    }

    try {
      const { periodo } = formData;

      const fileData = await generateRendicionPorCentroDeCostoReport({
        id: formData.centroCostoId,
        formato: formData.formato.value,
        mes: periodo.mes,
        anio: periodo.anio,
      });

      const selectedCentroCosto = allCentroCostos.find(cc => cc.id === formData.centroCostoId);

      const fileName = generateReportFileName({
        reporteBase: 'ReporteRendicionPorCentroCosto',
        identificador: selectedCentroCosto?.numeroCuenta.toString() || 'N-A',
        denominacion: selectedCentroCosto?.nombre || 'SinDenominacion',
        fecha: new Date(
          periodo.anio,
          periodo.mes - 1,
          1,
        ),
        extension: formData.formato.value,
      });

      // --- 3. Llamamos a la función de descarga del hook ---
      downloadFile(fileData, fileName);

      addNotification('Reporte generado exitosamente. La descarga ha comenzado.', 'success');
    } catch (error) {
      console.error('Error al generar el reporte:', error);
      addNotification('Hubo un error al generar el reporte.', 'error');
    } finally {
      setIsLoading(false);
    }
  }

  const handleClearForm = () => {
    setFormData({
      centroCostoId: null,
      formato: null,
      periodo: null,
    });
  };

  return (
    <form onSubmit={handleGenerateReport}>

      <Col className="mb-4">
        <Select
          label="Centro de Costo"
          name="centroCostoId"
          options={centroCostosOptions}
          value={centroCostosOptions.find((option) => option.value === formData.centroCostoId) || null}
          onChange={(option: SelectOption<number>) => {
            handleInputChange('centroCostoId', option?.value ?? null);
          }}
          placeholder={isLoadingCentroCostos ? 'Cargando Centro de Costos...' : 'Seleccione un Centro de costo'}
          isDisabled={isLoadingCentroCostos}
        />
      </Col>

      <Col className="mb-4">
        <FormatSelector
          value={formData.formato}
          onChange={(option) => handleInputChange('formato', option)}
        />
      </Col>

      <Row className="align-items-end">
        <Col md={6}>
          <Select
            label="Periodo"
            name="periodo"
            options={periodOptions}
            value={
              periodOptions.find(
                (option) => option.value?.equals(formData.periodo)
              ) || null
            }
            onChange={(option: SelectOption<Periodo>) => {
              handleInputChange('periodo', option?.value ?? null);
            }}
            placeholder="Seleccione un período"
          />
        </Col>

        <Col md={6}>
          <ReportFormActions
            onClear={handleClearForm}
            isLoading={isLoading}
          />
        </Col>
      </Row>

    </form>
  )
}
